/**
 * Enterprise-grade Error Classification System
 * 
 * Provides structured error handling for production environments with
 * detailed error classification, correlation tracking, and debugging context.
 */

/**
 * Base error interface for all service errors
 */
export interface ServiceErrorBase {
  readonly code: string;
  readonly message: string;
  readonly correlationId: string;
  readonly timestamp: Date;
  readonly context?: Record<string, unknown>;
  readonly retryable: boolean;
}

/**
 * Error codes for consistent error classification
 */
export enum ErrorCode {
  // Validation Errors (4xx equivalent)
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  REQUIRED_FIELD_MISSING = 'REQUIRED_FIELD_MISSING',
  INVALID_FORMAT = 'INVALID_FORMAT',
  DUPLICATE_ENTRY = 'DUPLICATE_ENTRY',
  
  // Permission Errors (403 equivalent)
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  ORGANIZATION_MISMATCH = 'ORGANIZATION_MISMATCH',
  USER_NOT_AUTHENTICATED = 'USER_NOT_AUTHENTICATED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  
  // Not Found Errors (404 equivalent)
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  TEMPLATE_NOT_FOUND = 'TEMPLATE_NOT_FOUND',
  USER_PREFERENCES_NOT_FOUND = 'USER_PREFERENCES_NOT_FOUND',
  ENTITY_NOT_FOUND = 'ENTITY_NOT_FOUND',
  
  // Network/Database Errors (5xx equivalent)
  DATABASE_CONNECTION_FAILED = 'DATABASE_CONNECTION_FAILED',
  DATABASE_TIMEOUT = 'DATABASE_TIMEOUT',
  DATABASE_CONSTRAINT_VIOLATION = 'DATABASE_CONSTRAINT_VIOLATION',
  SUPABASE_RPC_FAILED = 'SUPABASE_RPC_FAILED',
  DATABASE_QUERY_FAILED = 'DATABASE_QUERY_FAILED',
  DATABASE_UPDATE_FAILED = 'DATABASE_UPDATE_FAILED',
  DATABASE_INSERT_FAILED = 'DATABASE_INSERT_FAILED',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  
  // Concurrency Errors
  OPTIMISTIC_LOCK_FAILED = 'OPTIMISTIC_LOCK_FAILED',
  CONCURRENT_MODIFICATION = 'CONCURRENT_MODIFICATION',
  
  // Rate Limiting
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  
  // Migration Specific
  MIGRATION_ALREADY_COMPLETED = 'MIGRATION_ALREADY_COMPLETED',
  MIGRATION_IN_PROGRESS = 'MIGRATION_IN_PROGRESS',
  MIGRATION_INTEGRITY_CHECK_FAILED = 'MIGRATION_INTEGRITY_CHECK_FAILED',
  
  // General System Errors
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  FEATURE_DISABLED = 'FEATURE_DISABLED'
}

/**
 * Abstract base class for all service errors
 */
export abstract class ServiceError extends Error implements ServiceErrorBase {
  public readonly code: string;
  public readonly correlationId: string;
  public readonly timestamp: Date;
  public readonly context?: Record<string, unknown>;
  public readonly retryable: boolean;

  constructor(
    code: string,
    message: string,
    correlationId: string,
    retryable: boolean = false,
    context?: Record<string, unknown>
  ) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.correlationId = correlationId;
    this.timestamp = new Date();
    this.context = context;
    this.retryable = retryable;
    
    // Maintains proper stack trace for V8
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Convert error to JSON for logging/API responses
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      correlationId: this.correlationId,
      timestamp: this.timestamp.toISOString(),
      context: this.context,
      retryable: this.retryable,
      stack: this.stack
    };
  }

  /**
   * Create a user-friendly error message for UI display
   */
  abstract getUserMessage(): string;
}

/**
 * Validation errors - user input issues
 */
export class ValidationError extends ServiceError {
  constructor(
    message: string,
    correlationId: string,
    context?: Record<string, unknown>
  ) {
    super(ErrorCode.VALIDATION_FAILED, message, correlationId, false, context);
  }

  getUserMessage(): string {
    return 'Please check your input and try again.';
  }
}

/**
 * Permission errors - authorization issues
 */
export class PermissionError extends ServiceError {
  constructor(
    code: ErrorCode,
    message: string,
    correlationId: string,
    context?: Record<string, unknown>
  ) {
    super(code, message, correlationId, false, context);
  }

  getUserMessage(): string {
    return 'You do not have permission to perform this action.';
  }
}

/**
 * Not found errors - resource doesn't exist
 */
export class NotFoundError extends ServiceError {
  constructor(
    resource: string,
    correlationId: string,
    context?: Record<string, unknown>
  ) {
    super(
      ErrorCode.RESOURCE_NOT_FOUND,
      `${resource} not found`,
      correlationId,
      false,
      context
    );
  }

  getUserMessage(): string {
    return 'The requested item could not be found.';
  }
}

/**
 * Database/Network errors - potentially retryable
 */
export class DatabaseError extends ServiceError {
  constructor(
    code: ErrorCode,
    message: string,
    correlationId: string,
    retryable: boolean = true,
    context?: Record<string, unknown>
  ) {
    super(code, message, correlationId, retryable, context);
  }

  getUserMessage(): string {
    if (this.retryable) {
      return 'A temporary error occurred. Please try again in a moment.';
    }
    return 'A database error occurred. Please contact support if this persists.';
  }
}

/**
 * Concurrency errors - conflicts in concurrent operations
 */
export class ConcurrencyError extends ServiceError {
  constructor(
    message: string,
    correlationId: string,
    context?: Record<string, unknown>
  ) {
    super(ErrorCode.OPTIMISTIC_LOCK_FAILED, message, correlationId, true, context);
  }

  getUserMessage(): string {
    return 'This item was modified by another user. Please refresh and try again.';
  }
}

/**
 * Security errors - authentication and authorization issues
 */
export class SecurityError extends ServiceError {
  constructor(
    message: string,
    correlationId: string,
    context?: Record<string, unknown>
  ) {
    super(ErrorCode.UNAUTHORIZED, message, correlationId, false, context);
  }

  getUserMessage(): string {
    return 'Access denied. Please check your permissions.';
  }
}

/**
 * Migration specific errors
 */
export class MigrationError extends ServiceError {
  constructor(
    code: ErrorCode,
    message: string,
    correlationId: string,
    context?: Record<string, unknown>
  ) {
    super(code, message, correlationId, false, context);
  }

  getUserMessage(): string {
    switch (this.code) {
      case ErrorCode.MIGRATION_ALREADY_COMPLETED:
        return 'This migration has already been completed.';
      case ErrorCode.MIGRATION_IN_PROGRESS:
        return 'Migration is currently in progress. Please wait.';
      case ErrorCode.MIGRATION_INTEGRITY_CHECK_FAILED:
        return 'Migration data integrity check failed. Please contact support.';
      default:
        return 'An error occurred during migration. Please contact support.';
    }
  }
}

/**
 * System configuration errors
 */
export class ConfigurationError extends ServiceError {
  constructor(
    message: string,
    correlationId: string,
    context?: Record<string, unknown>
  ) {
    super(ErrorCode.CONFIGURATION_ERROR, message, correlationId, false, context);
  }

  getUserMessage(): string {
    return 'A system configuration error occurred. Please contact support.';
  }
}

/**
 * Rate limiting errors
 */
export class RateLimitError extends ServiceError {
  public readonly retryAfter: number; // seconds

  constructor(
    message: string,
    correlationId: string,
    retryAfter: number = 60,
    context?: Record<string, unknown>
  ) {
    super(ErrorCode.RATE_LIMIT_EXCEEDED, message, correlationId, true, context);
    this.retryAfter = retryAfter;
  }

  getUserMessage(): string {
    return `Too many requests. Please wait ${this.retryAfter} seconds before trying again.`;
  }
}

/**
 * Network errors - connectivity and service availability issues
 */
export class NetworkError extends ServiceError {
  constructor(
    code: ErrorCode,
    message: string,
    correlationId: string,
    retryable: boolean = true,
    context?: Record<string, unknown>
  ) {
    super(code, message, correlationId, retryable, context);
  }

  getUserMessage(): string {
    if (this.code === ErrorCode.SERVICE_UNAVAILABLE) {
      return 'Service is temporarily unavailable. Please try again later.';
    }
    return 'A network error occurred. Please check your connection and try again.';
  }
}

/**
 * Authentication errors
 */
export class AuthError extends ServiceError {
  constructor(
    code: ErrorCode,
    message: string,
    correlationId: string,
    context?: Record<string, unknown>
  ) {
    super(code, message, correlationId, false, context);
  }

  getUserMessage(): string {
    return 'Authentication failed. Please sign in again.';
  }
}

/**
 * Error factory for creating typed errors with correlation IDs
 */
export class ErrorFactory {
  /**
   * Create a validation error with automatic correlation ID
   */
  static validation(
    message: string,
    correlationId?: string,
    context?: Record<string, unknown>
  ): ValidationError {
    return new ValidationError(
      message,
      correlationId || this.generateCorrelationId(),
      context
    );
  }

  /**
   * Create a permission error with automatic correlation ID
   */
  static permission(
    code: ErrorCode,
    message: string,
    correlationId?: string,
    context?: Record<string, unknown>
  ): PermissionError {
    return new PermissionError(
      code,
      message,
      correlationId || this.generateCorrelationId(),
      context
    );
  }

  /**
   * Create a not found error with automatic correlation ID
   */
  static notFound(
    resource: string,
    correlationId?: string,
    context?: Record<string, unknown>
  ): NotFoundError {
    return new NotFoundError(
      resource,
      correlationId || this.generateCorrelationId(),
      context
    );
  }

  /**
   * Create a database error with automatic correlation ID
   */
  static database(
    code: ErrorCode,
    message: string,
    retryable: boolean = true,
    correlationId?: string,
    context?: Record<string, unknown>
  ): DatabaseError {
    return new DatabaseError(
      code,
      message,
      correlationId || this.generateCorrelationId(),
      retryable,
      context
    );
  }

  /**
   * Create a concurrency error with automatic correlation ID
   */
  static concurrency(
    message: string,
    correlationId?: string,
    context?: Record<string, unknown>
  ): ConcurrencyError {
    return new ConcurrencyError(
      message,
      correlationId || this.generateCorrelationId(),
      context
    );
  }

  /**
   * Create a migration error with automatic correlation ID
   */
  static migration(
    code: ErrorCode,
    message: string,
    correlationId?: string,
    context?: Record<string, unknown>
  ): MigrationError {
    return new MigrationError(
      code,
      message,
      correlationId || this.generateCorrelationId(),
      context
    );
  }

  /**
   * Create a network error with automatic correlation ID
   */
  static network(
    code: ErrorCode,
    message: string,
    retryable: boolean = true,
    correlationId?: string,
    context?: Record<string, unknown>
  ): NetworkError {
    return new NetworkError(
      code,
      message,
      correlationId || this.generateCorrelationId(),
      retryable,
      context
    );
  }

  /**
   * Create an auth error with automatic correlation ID
   */
  static auth(
    code: ErrorCode,
    message: string,
    correlationId?: string,
    context?: Record<string, unknown>
  ): AuthError {
    return new AuthError(
      code,
      message,
      correlationId || this.generateCorrelationId(),
      context
    );
  }

  /**
   * Create an error from unknown type
   */
  static unknown(
    error: unknown,
    correlationId?: string,
    context?: Record<string, unknown>
  ): ServiceError {
    const id = correlationId || this.generateCorrelationId();
    
    if (error instanceof ServiceError) {
      return error;
    }
    
    if (error instanceof Error) {
      return new DatabaseError(
        ErrorCode.INTERNAL_SERVER_ERROR,
        error.message,
        id,
        false,
        { ...context, originalError: error.name, stack: error.stack }
      );
    }
    
    return new DatabaseError(
      ErrorCode.INTERNAL_SERVER_ERROR,
      'An unknown error occurred',
      id,
      false,
      { ...context, originalError: String(error) }
    );
  }

  /**
   * Generate a unique correlation ID for error tracking
   */
  private static generateCorrelationId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}